import { useState, useCallback, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { get_deployment, usePost } from '../../../../API';
import { useChannel } from '../../../../Realtime';
import { useInterval } from '../../../../Util';
import { safeParse } from '../../../../Util';
import { getLatestInboundWhatsAppMessage, isWhatsAppWindowClosed, mapMessageKindToChannel } from './utils';
import type { ContactSelectorOption } from './types';
import type { SelectOptionType } from '@aidkitorg/component-library';

/**
 * User presence data for facepile
 */
export interface UserPresence {
  name: string;
  lastSeen: number;
  unsavedChanges?: boolean;
  browserTab?: string;
}

/**
 * Hook to track other users viewing the same case (facepile functionality)
 * @param caseId - The support case ID
 * @param currentUserName - The current user's name
 * @returns Array of other users currently viewing the case
 */
export const useOtherUsersViewing = (caseId: string, currentUserName: string) => {
  const [userList, setUserList] = useState<Record<string, UserPresence>>({});

  const channelName = get_deployment() + ':comms:' + caseId;

  const facePileCallback = useCallback((data: any) => {
    if (data.event === "heartbeat") {
      const userKey = data.data.name + '/' + data.data.browserTab;
      setUserList((lastSeen) => ({
        ...lastSeen,
        [userKey]: {
          lastSeen: Date.now(),
          ...data.data
        }
      }));
    }

    if (data.event === "remove_user") {
      const userKey = data.data.name + '/' + data.data.browserTab;
      setUserList((lastSeen) => {
        const newUsers = { ...lastSeen };
        delete newUsers[userKey];
        return newUsers;
      });
    }
  }, []);

  const sendData = useChannel({
    realm: 'facepile',
    name: channelName,
    onMessage: facePileCallback,
  });

  useEffect(() => {
    const handleBeforeUnload = () => {
      sendData({
        realm: 'facepile',
        channel: get_deployment() + ':comms:' + caseId,
        event: 'remove_user',
        data: {
          name: currentUserName,
          browserTab: 'Support Case'
        }
      });
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [sendData, currentUserName, caseId]);

  useInterval(() => {
    if (currentUserName && document.visibilityState === "visible") {
      sendData({
        realm: 'facepile',
        channel: channelName,
        event: 'heartbeat',
        data: {
          name: currentUserName,
          unsavedChanges: false,
          browserTab: 'Support Case'
        }
      });
    }
  }, 1000);

  // Filter out current user and show only other active users
  const allUserKeys = Object.keys(userList);
  const now = Date.now();

  const otherUsers = allUserKeys
    .filter(k => {
      const timeSinceLastSeen = now - userList[k].lastSeen;
      const isActive = timeSinceLastSeen < 20000;
      const isNotCurrentUser = userList[k].name !== currentUserName;
      return isActive && isNotCurrentUser;
    })
    .map((k) => userList[k]);

  return otherUsers;
};

/**
 * Hook to monitor WhatsApp 24-hour messaging window status
 * If the last inbound message from an applicant was more than 24 hours ago, the window is closed.
 * @param canSendWhatsApp - Whether WhatsApp can be sent at all
 * @param messages - Array of messages
 * @returns Whether the WhatsApp window is closed
 */
export const useWhatsAppWindow = (
  canSendWhatsApp: boolean,
  messages: any[]
): boolean => {
  const [whatsAppWindowClosed, setWhatsAppWindowClosed] = useState(false);

  useEffect(() => {
    if (!canSendWhatsApp) {
      setWhatsAppWindowClosed(false);
      return;
    }

    const latestInboundWhatsAppMessage = getLatestInboundWhatsAppMessage(messages);

    if (latestInboundWhatsAppMessage) {
      setWhatsAppWindowClosed(isWhatsAppWindowClosed(latestInboundWhatsAppMessage));
    } else {
      setWhatsAppWindowClosed(false);
    }
  }, [messages, canSendWhatsApp]);

  return whatsAppWindowClosed;
};

/**
 * Hook to manage contact selection state and options for alternate contacts
 * @param config - Configuration object with alternate_contact
 * @param contactInfo - Contact information dictionary
 * @param selectedChannel - Currently selected channel
 * @param contextLang - Current language
 * @returns Object with contact selector options and show flag
 */
export const useContactSelector = (
  config: any,
  contactInfo: any,
  selectedChannel: SelectOptionType | undefined,
  contextLang: string
) => {
  const [contactPhoneKey, setContactPhoneKey] = useState("phone_number");
  const [contactEmailKey, setContactEmailKey] = useState("email");
  const [contactNameKey, setContactNameKey] = useState("legal_name");

  const contactSelectorOptions: ContactSelectorOption[] = useMemo(() => {
    const alternateContacts = safeParse(config.alternate_contact || '[]', []);
    const currentChannel = selectedChannel?.value as 'email' | 'sms' | 'whatsapp' | undefined;

    return [
      {
        displayName: contactInfo?.['legal_name'] || 'Applicant',
        dropdownLabel: contactInfo?.['legal_name'] || 'Applicant',
        value: 'phone_number:email:legal_name',
        callback: () => {
          setContactPhoneKey("phone_number");
          setContactEmailKey("email");
          setContactNameKey("legal_name");
        },
        disabled: currentChannel === 'email'
          ? !contactInfo?.['email']
          : !contactInfo?.['phone_number'] && !contactInfo?.['phone']
      },
      ...alternateContacts
        .filter((ac: any) => contactInfo?.[ac.phone_key] || contactInfo?.[ac.email_key])
        .map((ac: {
          label: Record<string, string | undefined>,
          name_key: string,
          phone_key: string,
          email_key: string,
        }) => {
          const contactName = contactInfo?.[ac.name_key] || 'Unknown';
          const categoryLabel = ac.label[contextLang] || ac.label['en'];

          return {
            displayName: contactName,
            dropdownLabel: categoryLabel + ": " + contactName,
            value: `${ac.phone_key}:${ac.email_key}:${ac.name_key}`,
            callback: () => {
              setContactPhoneKey(ac.phone_key);
              setContactEmailKey(ac.email_key);
              setContactNameKey(ac.name_key);
            },
            disabled: currentChannel === 'email'
              ? !contactInfo?.[ac.email_key]
              : !contactInfo?.[ac.phone_key]
          };
        })
    ];
  }, [config.alternate_contact, contactInfo, contextLang, selectedChannel]);

  const showContactSelector = contactSelectorOptions.filter(opt => !opt.disabled).length > 1;

  // Reset to main contact when switching channels if current contact doesn't have the required field
  useEffect(() => {
    if (!selectedChannel) return;

    const currentChannel = selectedChannel.value as 'email' | 'sms' | 'whatsapp';
    const hasRequiredField = currentChannel === 'email'
      ? contactInfo?.[contactEmailKey]
      : contactInfo?.[contactPhoneKey];

    if (!hasRequiredField) {
      setContactPhoneKey("phone_number");
      setContactEmailKey("email");
      setContactNameKey("legal_name");
    }
  }, [selectedChannel, contactInfo, contactPhoneKey, contactEmailKey]);

  return {
    contactPhoneKey,
    contactEmailKey,
    contactNameKey,
    contactSelectorOptions,
    showContactSelector,
    setContactPhoneKey,
    setContactEmailKey,
    setContactNameKey
  };
};

/**
 * Hook to set initial channel based on most recent message type
 * @param messages - Array of messages
 * @param availableChannels - Array of available channels
 * @param L - Localization object
 * @returns Selected channel state and setter
 */
export const useInitialChannelSelection = (
  messages: any[],
  availableChannels: ('email' | 'sms' | 'whatsapp')[],
  L: any
) => {
  const [selectedChannel, setSelectedChannel] = useState<SelectOptionType | undefined>(undefined);
  const [hasSetInitialChannel, setHasSetInitialChannel] = useState(false);

  useEffect(() => {
    if (hasSetInitialChannel || messages.length === 0 || availableChannels.length === 0) {
      return;
    }

    const mostRecentMessage = messages[messages.length - 1];
    const preferredChannel = mapMessageKindToChannel(mostRecentMessage.kind);

    const channelToUse = (preferredChannel && availableChannels.includes(preferredChannel))
      ? preferredChannel
      : availableChannels[0];

    setSelectedChannel({
      label: channelToUse === 'email'
        ? L.applicant.comms.send_email
        : channelToUse === 'whatsapp'
          ? L.applicant.comms.send_whatsapp
          : L.applicant.comms.send_text,
      value: channelToUse,
    });

    setHasSetInitialChannel(true);
  }, [messages, availableChannels, hasSetInitialChannel, L.applicant.comms]);

  // Update selected channel when available channels change
  useEffect(() => {
    if (selectedChannel && !availableChannels.includes(selectedChannel.value as 'email' | 'sms' | 'whatsapp')) {
      const newChannel = availableChannels.length > 0 ? availableChannels[0] : 'sms';
      setSelectedChannel({
        label: newChannel === 'email'
          ? L.applicant.comms.send_email
          : newChannel === 'whatsapp'
            ? L.applicant.comms.send_whatsapp
            : L.applicant.comms.send_text,
        value: newChannel,
      });
    }
  }, [availableChannels, L.applicant.comms, selectedChannel]);

  return { selectedChannel, setSelectedChannel };
};

/**
 * Hook to show spinner only after a delay to prevent flashing on fast loads
 * @param isLoading - Whether content is loading
 * @param delayMs - Delay in milliseconds before showing spinner (default 300)
 * @returns Whether to show the spinner
 */
export const useDelayedSpinner = (
  isLoading: boolean | undefined,
  delayMs: number = 300
): boolean => {
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setShowSpinner(true);
      }, delayMs);
      return () => clearTimeout(timer);
    } else {
      setShowSpinner(false);
    }
  }, [isLoading, delayMs]);

  return showSpinner;
};

/**
 * Hook to automatically scroll message container to bottom on new messages
 * @param messages - Array of messages
 * @param isLoading - Whether messages are loading
 * @returns Ref to attach to the scrollable container
 */
export const useAutoScroll = (
  messages: any[],
  isLoading: boolean | undefined
) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const previousMessageCountRef = useRef<number>(0);

  // Scroll to bottom when messages load or change
  useLayoutEffect(() => {
    if (messagesContainerRef.current && messages.length > 0 && !isLoading) {
      const container = messagesContainerRef.current;
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 100;

      if (isNearBottom || container.scrollTop === 0) {
        container.scrollTop = container.scrollHeight;
      }
    }
  }, [messages, isLoading]);

  // Force scroll to bottom when new message is sent
  useEffect(() => {
    if (messages.length > previousMessageCountRef.current && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
    previousMessageCountRef.current = messages.length;
  }, [messages.length]);

  return messagesContainerRef;
};

/**
 * Hook to manage call state and actions
 * @param phoneNumber - The phone number to call
 * @param contactName - The name of the contact
 * @param twilioVoiceChannel - The Twilio voice channel to use
 * @param requireConsentToRecordCalls - Configuration for consent to record
 * @returns Call state and action functions
 */
export const useCallManager = (
  phoneNumber: string,
  contactName: string,
  twilioVoiceChannel: string,
  requireConsentToRecordCalls?: { kind: string, customConsentScript?: Record<string, string> }
) => {
  const [callState, setCallState] = useState<'idle' | 'starting' | 'active' | 'conference'>('idle');
  const [activeCallInfo, setActiveCallInfo] = useState<{
    phoneNumber: string;
    contactName: string;
    startTime: number;
    duration: string;
    callSid?: string;
    isMuted: boolean;
    isConference: boolean;
  } | null>(null);
  const [showKeypad, setShowKeypad] = useState(false);

  // Consent to record states
  const [showingConsentBox, setShowingConsentBox] = useState<'notice' | 'script' | null>(null);
  const [callAnswered, setCallAnswered] = useState(false);
  const [consentGranted, setConsentGranted] = useState<boolean | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [callInProgress, setCallInProgress] = useState(false);

  // Conference participant states
  const [applicantCallSid, setApplicantCallSid] = useState('');
  const [translatorCallSid, setTranslatorCallSid] = useState('');
  const [conferenceNumbersSids, setConferenceNumbersSids] = useState<Record<string, string>>({});
  const [conferenceNumberStatuses, setConferenceNumberStatuses] = useState<Record<string, string>>({});
  const [conferenceNumbersLoading, setConferenceNumbersLoading] = useState<Record<string, boolean>>({});
  const [applicantConfStatus, setApplicantConfStatus] = useState('');
  const [translatorConfStatus, setTranslatorConfStatus] = useState('');
  const [applicantLoading, setApplicantLoading] = useState(false);
  const [translatorLoading, setTranslatorLoading] = useState(false);

  // Refs to keep current values for use in callbacks
  const callAnsweredRef = useRef(callAnswered);
  const consentGrantedRef = useRef(consentGranted);

  useEffect(() => {
    callAnsweredRef.current = callAnswered;
    consentGrantedRef.current = consentGranted;
  }, [callAnswered, consentGranted]);

  const getApplicantCalls = usePost('/voice/get_applicant_calls');
  const addToConferenceAPI = usePost('/voice/add_to_conference');
  const removeFromConferenceAPI = usePost('/voice/remove_from_conference');
  const startRecording = usePost('/voice/start_recording');
  const saveUnrecordedOutboundCall = usePost('/voice/save_unrecorded_call');
  const callStatus = usePost('/voice/call_status');

  /**
   * Reset consent state
   */
  const resetConsentState = useCallback(() => {
    setCallAnswered(false);
    setConsentGranted(null);
    setIsRecording(false);
    setShowingConsentBox(null);
  }, []);

  /**
   * Record the call
   */
  const recordCall = useCallback(async (call: any) => {
    if (!call) return;

    try {
      const res = await startRecording({
        callAgent: call.customParameters.get('agent'),
        callSid: call.parameters.CallSid,
        to: phoneNumber,
        channel: twilioVoiceChannel,
      });

      if (res && !('error' in res)) {
        setIsRecording(true);
      }
    } catch (e) {
      console.error('Error recording call:', e);

      // Try to save unrecorded call info if recording fails
      await saveUnrecordedOutboundCall({
        callAgent: call.customParameters.get('agent'),
        callSid: call.parameters.CallSid,
        to: phoneNumber,
        channel: twilioVoiceChannel,
        status: 'recording_error',
      });
    }
  }, [phoneNumber, twilioVoiceChannel, startRecording, saveUnrecordedOutboundCall]);

  /**
   * End conference if removing last participant
   */
  const endConfOnRemoveLastParticipant = useCallback((sidToRemove: string, twilioCall: any) => {
    const allSids = [applicantCallSid, translatorCallSid, ...Object.values(conferenceNumbersSids)].filter(
      sid => !!sid && sid !== sidToRemove
    );

    if (allSids.length === 0 && twilioCall) {
      twilioCall.disconnect();
    }
  }, [applicantCallSid, translatorCallSid, conferenceNumbersSids]);

  /**
   * Add participant to conference
   */
  const addParticipantToConference = useCallback(async (
    participantType: 'applicant' | 'translator' | 'conferenceNumber',
    phoneNumberToAdd?: string,
    dialCode?: string
  ) => {
    const conferenceId = phoneNumber.replace('+', '');

    if (participantType === 'applicant') {
      setApplicantLoading(true);
      const response = await addToConferenceAPI({
        to: phoneNumber,
        voiceChannel: twilioVoiceChannel,
        conferenceName: conferenceId
      });
      if (typeof response === 'string') {
        setApplicantCallSid(response);
      }
      setApplicantLoading(false);
    } else if (participantType === 'translator') {
      setTranslatorLoading(true);
      const response = await addToConferenceAPI({
        to: 'translationService',
        voiceChannel: twilioVoiceChannel,
        conferenceName: conferenceId
      });
      if (typeof response === 'string') {
        setTranslatorCallSid(response);
      }
      setTranslatorLoading(false);
    } else if (participantType === 'conferenceNumber' && phoneNumberToAdd) {
      setConferenceNumbersLoading(prev => ({ ...prev, [phoneNumberToAdd]: true }));
      const response = await addToConferenceAPI({
        to: phoneNumberToAdd,
        voiceChannel: twilioVoiceChannel,
        conferenceName: conferenceId,
        dialCode: dialCode || ''
      });
      if (typeof response === 'string') {
        setConferenceNumbersSids(prev => ({ ...prev, [phoneNumberToAdd]: response }));
      }
      setConferenceNumbersLoading(prev => ({ ...prev, [phoneNumberToAdd]: false }));
    }
  }, [phoneNumber, twilioVoiceChannel, addToConferenceAPI]);

  /**
   * Remove participant from conference
   */
  const removeParticipantFromConference = useCallback(async (
    participantType: 'applicant' | 'translator' | 'conferenceNumber',
    twilioCall: any,
    phoneNumberToRemove?: string
  ) => {
    if (participantType === 'applicant') {
      setApplicantLoading(true);
      endConfOnRemoveLastParticipant(applicantCallSid, twilioCall);
      await removeFromConferenceAPI({ toRemove: applicantCallSid });
      setApplicantCallSid('');
      setApplicantLoading(false);
    } else if (participantType === 'translator') {
      setTranslatorLoading(true);
      endConfOnRemoveLastParticipant(translatorCallSid, twilioCall);
      await removeFromConferenceAPI({ toRemove: translatorCallSid });
      setTranslatorCallSid('');
      setTranslatorLoading(false);
    } else if (participantType === 'conferenceNumber' && phoneNumberToRemove) {
      const sidToRemove = conferenceNumbersSids[phoneNumberToRemove];
      setConferenceNumbersLoading(prev => ({ ...prev, [phoneNumberToRemove]: true }));
      endConfOnRemoveLastParticipant(sidToRemove, twilioCall);
      await removeFromConferenceAPI({ toRemove: sidToRemove });
      setConferenceNumbersSids(prev => {
        const newSids = { ...prev };
        delete newSids[phoneNumberToRemove];
        return newSids;
      });
      setConferenceNumbersLoading(prev => ({ ...prev, [phoneNumberToRemove]: false }));
    }
  }, [applicantCallSid, translatorCallSid, conferenceNumbersSids, removeFromConferenceAPI, endConfOnRemoveLastParticipant]);

  /**
   * Poll for in-progress calls
   */
  useEffect(() => {
    let mounted = true;
    const checkCallInProgress = async () => {
      const response = await getApplicantCalls({ to: phoneNumber });
      if (mounted && response?.inProgressCalls) {
        setCallInProgress(response.inProgressCalls.length > 0);
      }
    };
    checkCallInProgress();

    const interval = setInterval(async () => {
      const response = await getApplicantCalls({ to: phoneNumber });
      if (mounted && response?.inProgressCalls) {
        setCallInProgress(response.inProgressCalls.length > 0);
      }
    }, 5000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [phoneNumber, getApplicantCalls]);

  /**
   * Poll applicant call status
   */
  useEffect(() => {
    if (!applicantCallSid) return;

    let mounted = true;
    const interval = setInterval(async () => {
      const response = await callStatus({ callSid: applicantCallSid });
      if (mounted && response) {
        setApplicantConfStatus(typeof response === 'string' ? response : (response as any).status || '');
      }
    }, 1000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [applicantCallSid, callStatus]);

  /**
   * Poll translator call status
   */
  useEffect(() => {
    if (!translatorCallSid) return;

    let mounted = true;
    const interval = setInterval(async () => {
      const response = await callStatus({ callSid: translatorCallSid });
      if (mounted && response) {
        setTranslatorConfStatus(typeof response === 'string' ? response : (response as any).status || '');
      }
    }, 1000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [translatorCallSid, callStatus]);

  /**
   * Poll conference numbers call statuses
   */
  useEffect(() => {
    const intervals: Record<string, ReturnType<typeof setInterval>> = {};

    Object.keys(conferenceNumbersSids).forEach(key => {
      const callSid = conferenceNumbersSids[key];
      if (!callSid) return;

      intervals[key] = setInterval(async () => {
        const response = await callStatus({ callSid });
        if (response) {
          const status = typeof response === 'string' ? response : (response as any).status || '';
          setConferenceNumberStatuses(prev => ({
            ...prev,
            [key]: status
          }));
        }
      }, 1000);
    });

    return () => {
      Object.values(intervals).forEach(clearInterval);
    };
  }, [conferenceNumbersSids, callStatus]);

  /**
   * Start a regular call
   */
  const startCall = useCallback(async (twilioDevice: any, twilioCaller: string | null, twilioCall: any, setTwilioCall: (call: any) => void) => {
    if (!twilioDevice || !phoneNumber) return;

    // Check for in-progress calls
    const calls = await getApplicantCalls({ to: phoneNumber });
    if (!calls) {
      throw new Error('Failed to check for in-progress calls');
    }

    if (calls.inProgressCalls && calls.inProgressCalls.length > 0) {
      throw new Error('Call already in progress');
    }

    setCallState('starting');

    // Initiate the call
    const call = await twilioDevice.connect({
      params: {
        To: phoneNumber,
        ...(twilioCaller && { agent: twilioCaller })
      }
    });

    call.on('accept', () => {
      setCallState('active');
      setActiveCallInfo({
        phoneNumber,
        contactName,
        startTime: Date.now(),
        duration: '00:00',
        isMuted: false,
        isConference: false
      });

      // If consent is required, mark call as answered
      if (requireConsentToRecordCalls) {
        setCallAnswered(true);
      }
    });

    call.on('disconnect', () => {
      setCallState('idle');
      setActiveCallInfo(null);
      setTwilioCall(null);
      setCallInProgress(false);

      // Save unrecorded call if consent was required but not indicated
      if (requireConsentToRecordCalls) {
        if (callAnsweredRef.current && consentGrantedRef.current === null) {
          saveUnrecordedOutboundCall({
            callAgent: call.customParameters.get('agent'),
            callSid: call.parameters.CallSid,
            to: phoneNumber,
            channel: twilioVoiceChannel,
            status: 'consent_unknown',
          });
        }
        resetConsentState();
      }
    });

    call.on('cancel', () => {
      setCallState('idle');
      setActiveCallInfo(null);
      setTwilioCall(null);

      if (requireConsentToRecordCalls) {
        resetConsentState();
      }
    });

    setTwilioCall(call);
  }, [phoneNumber, contactName, getApplicantCalls, requireConsentToRecordCalls, twilioVoiceChannel, resetConsentState, saveUnrecordedOutboundCall]);

  /**
   * Start a conference call
   */
  const startConferenceCall = useCallback(async (twilioDevice: any, twilioCaller: string | null, twilioCall: any, setTwilioCall: (call: any) => void) => {
    if (!twilioDevice || !phoneNumber) return;

    // Check for in-progress calls
    const calls = await getApplicantCalls({ to: phoneNumber });
    if (!calls) {
      throw new Error('Failed to check for in-progress calls');
    }

    if (calls.inProgressCalls && calls.inProgressCalls.length > 0) {
      throw new Error('Call already in progress');
    }

    setCallState('starting');

    // Use phone number without + as conference ID
    const conferenceId = phoneNumber.replace('+', '');

    const call = await twilioDevice.connect({
      params: {
        To: phoneNumber,
        callType: 'start_conference',
        conferenceId,
        ...(twilioCaller && { agent: twilioCaller })
      }
    });

    call.on('accept', () => {
      setCallState('conference');
      setActiveCallInfo({
        phoneNumber,
        contactName,
        startTime: Date.now(),
        duration: '00:00',
        isMuted: false,
        isConference: true
      });
    });

    call.on('disconnect', () => {
      setCallState('idle');
      setActiveCallInfo(null);
      setTwilioCall(null);
    });

    call.on('cancel', () => {
      setCallState('idle');
      setActiveCallInfo(null);
      setTwilioCall(null);
    });

    setTwilioCall(call);
  }, [phoneNumber, contactName, getApplicantCalls]);

  /**
   * Hang up the active call
   */
  const hangup = useCallback((twilioCall: any) => {
    if (twilioCall) {
      twilioCall.disconnect();
    }
    setCallState('idle');
    setActiveCallInfo(null);
  }, []);

  /**
   * Toggle mute on the active call
   */
  const toggleMute = useCallback((twilioCall: any) => {
    if (!twilioCall || !activeCallInfo) return;

    const newMutedState = !activeCallInfo.isMuted;
    twilioCall.mute(newMutedState);

    setActiveCallInfo({
      ...activeCallInfo,
      isMuted: newMutedState
    });
  }, [activeCallInfo]);

  /**
   * Send DTMF digit
   */
  const sendDigit = useCallback((twilioCall: any, digit: string) => {
    if (twilioCall) {
      twilioCall.sendDigits(digit);
    }
  }, []);

  /**
   * Update call duration
   */
  useEffect(() => {
    if (!activeCallInfo) return;

    const updateDuration = () => {
      const elapsed = Math.floor((Date.now() - activeCallInfo.startTime) / 1000);
      const minutes = Math.floor(elapsed / 60).toString().padStart(2, '0');
      const seconds = (elapsed % 60).toString().padStart(2, '0');

      setActiveCallInfo(prev => prev ? {
        ...prev,
        duration: `${minutes}:${seconds}`
      } : null);
    };

    const interval = setInterval(updateDuration, 500);
    return () => clearInterval(interval);
  }, [activeCallInfo?.startTime]);

  return {
    callState,
    activeCallInfo,
    showKeypad,
    setShowKeypad,
    startCall,
    startConferenceCall,
    hangup,
    toggleMute,
    sendDigit,
    // Consent and recording state
    showingConsentBox,
    setShowingConsentBox,
    callAnswered,
    consentGranted,
    setConsentGranted,
    isRecording,
    callInProgress,
    recordCall,
    resetConsentState,
    saveUnrecordedOutboundCall,
    // Conference participant state
    applicantCallSid,
    translatorCallSid,
    conferenceNumbersSids,
    conferenceNumberStatuses,
    conferenceNumbersLoading,
    applicantConfStatus,
    translatorConfStatus,
    applicantLoading,
    translatorLoading,
    addParticipantToConference,
    removeParticipantFromConference
  };
};
